/**
 * PWAInstallBanner — dismissible banner prompting users to install the app.
 * Shows at bottom of screen above the mobile bottom nav.
 * Only appears when browser supports PWA install and user hasn't dismissed.
 * iOS: shows guided overlay with Safari share instructions.
 * Android/Chrome: triggers native install prompt directly.
 */
import { useState } from 'react'
import { Download, X, Share } from 'lucide-react'
import { usePWAInstall } from '../hooks/usePWAInstall'
import { useT } from '@/i18n'

export function PWAInstallBanner() {
  const t = useT()
  const { showBanner, isIOS, promptInstall, dismissBanner } = usePWAInstall()
  const [showIOSGuide, setShowIOSGuide] = useState(false)

  if (!showBanner) return null

  const handleInstallClick = () => {
    if (isIOS) {
      setShowIOSGuide(true)
    } else {
      promptInstall()
    }
  }

  return (
    <>
      {/* Banner */}
      <div
        className="fixed left-3 right-3 z-[60] animate-in slide-in-from-bottom"
        style={{ bottom: 'calc(56px + env(safe-area-inset-bottom, 0px) + 8px)' }}
      >
        <div
          className="flex items-center gap-3 px-4 py-3 rounded-2xl"
          style={{
            background: 'linear-gradient(140deg, var(--y-accent), var(--y-accent-secondary))',
            boxShadow: 'var(--y-shadow-fab)',
          }}
        >
          <Download className="w-5 h-5 text-white flex-shrink-0" strokeWidth={2} />
          <div className="flex-1 min-w-0">
            <p style={{ fontSize: '13px', fontWeight: 700, color: '#fff', letterSpacing: '-0.01em' }}>
              {t('navigation.appearance.bannerTitle')}
            </p>
            <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', marginTop: '1px' }}>
              {t('navigation.appearance.bannerDescription')}
            </p>
          </div>
          <button
            onClick={handleInstallClick}
            className="px-3 py-1.5 rounded-lg flex-shrink-0"
            style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)', fontSize: '12px', fontWeight: 600, color: '#fff' }}
          >
            {t('navigation.appearance.install')}
          </button>
          <button
            onClick={dismissBanner}
            className="w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0"
            style={{ background: 'rgba(255,255,255,0.15)' }}
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5 text-white" strokeWidth={2} />
          </button>
        </div>
      </div>

      {/* iOS Install Guide Overlay */}
      {showIOSGuide && (
        <div className="fixed inset-0 z-[200]" onClick={() => setShowIOSGuide(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className="absolute bottom-0 left-0 right-0 bg-[var(--y-surface-primary)] rounded-t-3xl p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-10 h-1 rounded-full bg-[var(--y-border)] mx-auto mb-5" />
            <h3 className="text-lg font-bold text-[var(--y-text-primary)] mb-4">
              {t('navigation.appearance.installApp')}
            </h3>

            {/* Step 1 */}
            <div className="flex items-start gap-3 mb-4">
              <div className="w-8 h-8 rounded-full bg-[var(--y-accent)]/15 flex items-center justify-center shrink-0">
                <span className="text-sm font-bold" style={{ color: 'var(--y-accent)' }}>1</span>
              </div>
              <div className="flex-1">
                <p className="text-[var(--y-text-primary)] font-medium flex items-center gap-1.5">
                  {t('navigation.appearance.iosInstallGuide').split('↑')[0]}
                  <Share className="w-4 h-4 inline" style={{ color: 'var(--y-accent)' }} />
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex items-start gap-3 mb-6">
              <div className="w-8 h-8 rounded-full bg-[var(--y-accent)]/15 flex items-center justify-center shrink-0">
                <span className="text-sm font-bold" style={{ color: 'var(--y-accent)' }}>2</span>
              </div>
              <div className="flex-1">
                <p className="text-[var(--y-text-primary)] font-medium">
                  &ldquo;{t('navigation.appearance.addToHomeScreen')}&rdquo;
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowIOSGuide(false)}
              className="w-full py-3 rounded-xl text-center font-medium bg-[var(--y-surface-secondary)] text-[var(--y-text-primary)]"
            >
              {t('common.action.gotIt')}
            </button>
          </div>
        </div>
      )}
    </>
  )
}
